import { useState } from 'react';
import { Calendar as CalendarIcon, Clock, CheckCircle, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const timeSlots = [
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00', '18:30',
];

export default function AppointmentPage() {
  useSeo({
    title: 'Prenota un Appuntamento Privato',
    description:
      "Prenota un appuntamento nello showroom Ciro Gallotti a Napoli. Consulenza personalizzata, presentazione delle collezioni esclusive, esperienza su misura.",
    path: '/appuntamento',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Appuntamento privato in showroom',
      provider: { '@type': 'JewelryStore', name: 'Ciro Gallotti' },
      areaServed: { '@type': 'City', name: 'Napoli' },
      description: 'Consulenza orafa privata su appuntamento presso lo showroom Ciro Gallotti.',
      url: `${SITE_URL}/appuntamento`,
    },
  });

  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', time: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleTimeChange = (value) => setFormData({ ...formData, time: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error('Seleziona una data'); return; }
    if (!formData.time) { toast.error('Seleziona un orario'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        date: format(date, 'yyyy-MM-dd'), time: formData.time, message: formData.message,
      });
      setSubmitted(true);
      toast.success('Appuntamento prenotato con successo');
    } catch (e) {
      toast.error('Errore nella prenotazione');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory pt-24 md:pt-28" data-testid="appointment-page">
      <header className="px-6 md:px-12 lg:px-16 py-12 md:py-20 hairline-b">
        <div className="max-w-container mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">Appuntamento Privato</p>
          </div>
          <h1 className="font-display text-h1 text-ink">
            Prenota <span className="italic">Appuntamento</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink/65 leading-relaxed">
            Riserva un appuntamento privato nel nostro showroom per scoprire le collezioni esclusive
            e ricevere una consulenza personalizzata dai nostri maestri orafi.
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-20">
        {submitted ? (
          <div className="text-center py-12" data-testid="appointment-success">
            <div className="w-16 h-16 border border-bordeaux rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-8 h-8 text-bordeaux" strokeWidth={1.25} />
            </div>
            <h2 className="font-display text-h2 text-ink mb-4">Appuntamento Confermato</h2>
            <p className="text-muted leading-relaxed mb-2">Il tuo appuntamento è prenotato per il</p>
            <p className="font-display text-h3 text-bordeaux mb-8">
              {date && format(date, 'd MMMM yyyy', { locale: it })} alle {formData.time}
            </p>
            <p className="text-small text-muted mb-10">
              Riceverai una email di conferma con tutti i dettagli.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setDate(null);
                setFormData({ name: '', email: '', phone: '', time: '', message: '' });
              }}
              variant="outline"
            >
              Prenota un altro appuntamento
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-10" data-testid="appointment-form">
            {/* Date & time */}
            <section>
              <h3 className="font-display text-h3 text-ink mb-2 flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-bordeaux" strokeWidth={1.5} />
                Data e Orario
              </h3>
              <p className="text-small text-muted mb-6">
                Disponibilità dal lunedì al sabato. La domenica siamo chiusi.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-eyebrow text-ink">Data *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal mt-2 h-11"
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-bordeaux" />
                        {date ? format(date, 'PPP', { locale: it }) : 'Seleziona una data'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-ivory border-line" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date() || d.getDay() === 0}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-eyebrow text-ink">Orario *</Label>
                  <Select value={formData.time} onValueChange={handleTimeChange}>
                    <SelectTrigger className="mt-2 h-11 rounded-sm border-line" data-testid="time-select-trigger">
                      <SelectValue placeholder="Seleziona orario">
                        {formData.time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-bordeaux" />
                            {formData.time}
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Personal info */}
            <section>
              <h3 className="font-display text-h3 text-ink mb-2 flex items-center gap-3">
                <User className="w-5 h-5 text-bordeaux" strokeWidth={1.5} />
                I Tuoi Dati
              </h3>
              <p className="text-small text-muted mb-6">
                Useremo questi contatti solo per confermare l'appuntamento.
              </p>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-eyebrow text-ink">Nome Completo *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange}
                    required className="mt-2" data-testid="appointment-name" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-eyebrow text-ink">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email}
                      onChange={handleChange} required className="mt-2" data-testid="appointment-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-eyebrow text-ink">Telefono *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone}
                      onChange={handleChange} required className="mt-2" data-testid="appointment-phone" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-eyebrow text-ink">Note (opzionale)</Label>
                  <Textarea id="message" name="message" value={formData.message}
                    onChange={handleChange} rows={4} className="mt-2 resize-none"
                    placeholder="Raccontaci cosa ti interessa vedere o se hai richieste particolari…"
                    data-testid="appointment-message" />
                </div>
              </div>
            </section>

            <Button type="submit" disabled={submitting} size="xl" variant="accent"
              className="w-full" data-testid="appointment-submit">
              {submitting ? (
                <span className="flex items-center gap-3">
                  <div className="w-4 h-4 border-2 border-ivory/30 border-t-ivory rounded-full animate-spin" />
                  Prenotazione in corso…
                </span>
              ) : (
                <span className="flex items-center gap-3">
                  <CalendarIcon className="w-4 h-4" />
                  Conferma Appuntamento
                </span>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
